import { Button } from '@parrot-software/design-system';
import { CircleHelp, Plus } from '@parrot-software/design-system/icons';

interface ActionButton {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  loading?: boolean;
  loadingLabel?: string;
}

interface SecondaryHeaderProps {
  title: string;
  description: string;
  actionButton?: ActionButton;
  showHelpIcon?: boolean;
  formId?: string;
}

/**
 * Secondary Header Component
 *
 * Page-level header for specific content (e.g., "Roles", "Crear rol")
 * Smaller font size, can include action button
 *
 * Note: Breadcrumbs are now rendered separately as their own grid area.
 * Use <Breadcrumbs /> component directly in PageLayout before SecondaryHeader.
 *
 * @param {SecondaryHeaderProps} props - Component properties
 * @param {string} props.title - Page title
 * @param {string} props.description - Page description
 * @param {ActionButton} [props.actionButton] - Optional action button
 * @param {boolean} [props.showHelpIcon=false] - Whether to show help icon
 * @returns {React.ReactElement} Secondary header component
 *
 * @example
 * // Simple header
 * <SecondaryHeader
 *   title="Roles"
 *   description="Crea roles con permisos específicos..."
 * />
 *
 * @example
 * // With action button
 * <SecondaryHeader
 *   title="Crear rol"
 *   description="Crea un rol para tu equipo..."
 *   actionButton={{
 *     label: "Crear rol",
 *     onClick: handleSubmit
 *   }}
 * />
 */
export const SecondaryHeader = ({
  title,
  description,
  actionButton,
  showHelpIcon = false,
  formId,
}: SecondaryHeaderProps): React.ReactElement => {
  return (
    <div className="flex items-start justify-between">
      <div className="flex flex-col gap-0.5">
        {/* Title with optional help icon */}
        <div className="flex items-center gap-2">
          <h2 className="text-xl font-semibold text-foreground">{title}</h2>
          {showHelpIcon && <CircleHelp className="size-4 text-muted-foreground cursor-help" />}
        </div>

        {/* Description */}
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>

      {/* Action Button */}
      {actionButton && (
        <Button
          disabled={actionButton.disabled || actionButton.loading}
          form={formId ?? ''}
          onClick={actionButton.onClick}
          type={formId ? 'submit' : 'button'}
          variant="default"
        >
          <Plus className="size-4 text-muted-foreground" color="white" strokeWidth={3.5} />
          {actionButton.loading ? actionButton.loadingLabel : actionButton.label}
        </Button>
      )}
    </div>
  );
};
